import { largeCurrencyFormat, twoDecimalPercentFormat, xStandardSuffixFormat } from 'common/formats/formats';
import { getTotalPublishedValuationsExpression } from 'pages/Firms/hooks/useFirmSummarySheet/config/createParser/utils/getTotalPublishedValuationsExpression';
import {
  FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL,
  FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL,
  FUND_SUMMARY_SPREADSHEET_TOTAL,
} from 'pages/Funds/common/constants/fundSummary/sheetAliases';
import { HandleTotalsCellsParams } from './handleTotalsCells.types';

const handleTotalsCells = (params: HandleTotalsCellsParams) => {
  const {
    alias,
    allPublishedValuationsExpression,
    alphabet,
    cell,
    colNumber,
    directAndFundInvestmentsTotalExpression,
    directInvestmentsExpression,
    FUND_SUMMARY_SPREADSHEET_COLUMNS,
    fundGrossIRR,
    portfolioCompaniesFundGrossIRR,
    fundInvestmentsExpression,
    subTotalValueKey,
    totalPublishedDirectInvestedCapitalExpression,
    totalPublishedFundInvestedCapitalExpression,
  } = params;

  switch (alias) {
    // Direct investments totals
    case FUND_SUMMARY_SPREADSHEET_DIRECT_INVESTMENTS_TOTAL:
      switch (colNumber) {
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL:
          Object.assign(cell, {
            expr: `=SUM(${directInvestmentsExpression})`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE:
          Object.assign(cell, {
            expr: `=${getTotalPublishedValuationsExpression({
              allPublishedValuationsExpression,
              alphabet,
              currentColumn: FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE,
            })}`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY:
          Object.assign(cell, {
            expr: `=${getTotalPublishedValuationsExpression({
              allPublishedValuationsExpression,
              alphabet,
              currentColumn: FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY,
            })}`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE:
          Object.assign(cell, {
            expr: `=${getTotalPublishedValuationsExpression({
              allPublishedValuationsExpression,
              alphabet,
              currentColumn: FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE,
            })}`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.GROSS_IRR:
          Object.assign(cell, {
            format: twoDecimalPercentFormat({}),
            gridType: 'percentage',
            value: portfolioCompaniesFundGrossIRR,
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.MOIC:
          Object.assign(cell, {
            // Total X Factor or MOIC = Total of Total Value (only Published) / Total Invested Capital (only Published)
            expr: `=IF(${totalPublishedDirectInvestedCapitalExpression}>0,${subTotalValueKey}/${totalPublishedDirectInvestedCapitalExpression},0)`,
            format: xStandardSuffixFormat,
            gridType: 'number',
          });
          break;

        default:
          break;
      }
      break;

    // Fund investments totals
    case FUND_SUMMARY_SPREADSHEET_FUND_INVESTMENTS_TOTAL:
      switch (colNumber) {
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY:
          Object.assign(cell, {
            expr: `=SUM(${fundInvestmentsExpression})`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE:
          Object.assign(cell, {
            expr: `=SUM(${fundInvestmentsExpression})`,
            format: largeCurrencyFormat,
            gridType: 'number',
            hidden: true,
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.GROSS_IRR:
          Object.assign(cell, {
            format: twoDecimalPercentFormat({}),
            gridType: 'percentage',
            value: 0,
            hidden: true,
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.MOIC:
          Object.assign(cell, {
            // Total X Factor or MOIC = Total of Total Value (only Published) / Total Invested Capital (only Published)
            expr: `=IF(${totalPublishedFundInvestedCapitalExpression}>0,${subTotalValueKey}/${totalPublishedFundInvestedCapitalExpression},0)`,
            format: xStandardSuffixFormat,
            gridType: 'number',
            hidden: true,
          });
          break;

        default:
          break;
      }
      break;

    // Total
    case FUND_SUMMARY_SPREADSHEET_TOTAL:
      switch (colNumber) {
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.INVESTED_CAPITAL:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.REALIZED_VALUE:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.UNREALIZED_EQUITY:
        case FUND_SUMMARY_SPREADSHEET_COLUMNS.TOTAL_VALUE:
          Object.assign(cell, {
            expr: `=SUM(${directAndFundInvestmentsTotalExpression})`,
            format: largeCurrencyFormat,
            gridType: 'number',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.GROSS_IRR:
          Object.assign(cell, {
            value: fundGrossIRR,
            format: twoDecimalPercentFormat({}),
            gridType: 'percentage',
          });
          break;

        case FUND_SUMMARY_SPREADSHEET_COLUMNS.MOIC:
          Object.assign(cell, {
            expr: `=SUM(${directAndFundInvestmentsTotalExpression})`,
            format: xStandardSuffixFormat,
            gridType: 'number',
          });
          break;

        default:
          break;
      }
      break;

    default:
      break;
  }
};

export default handleTotalsCells;
